import { ethers } from 'ethers';

/**
 * HyperCore service for interacting with HyperCore API
 * Based on: https://developers.circle.com/cctp/withdraw-usdc-from-hypercore-to-hyperevm
 */

export interface HyperCoreWithdrawParams {
  amount: string; // Amount in USDC (regular units, e.g., "10" for 10 USDC)
  sourceDex: 'spot' | 'perps'; // Source DEX on HyperCore
  hyperliquidChain: 'Mainnet' | 'Testnet';
  signatureChainId: string; // Chain ID in hex format (e.g., "0x3e7" for HyperEVM mainnet)
  signer: ethers.Signer; // Signer for EIP-712 signing
}

export interface HyperCoreAction {
  type: string;
  hyperliquidChain: string;
  signatureChainId: string;
  destination: string;
  sourceDex?: string;
  destinationDex: string;
  token: string;
  amount: string;
  fromSubAccount: null;
  nonce: number;
}

export interface HyperCoreSignature {
  r: string;
  s: string;
  v: number;
}

class HyperCoreService {
  private readonly USDC_SYSTEM_ADDRESS = '0x2000000000000000000000000000000000000000';
  // HyperCore USDC token addresses (different from HyperEVM USDC addresses)
  // Format: "USDC:0x..." - this is the HyperCore USDC address, not HyperEVM
  // Note: These are the HyperCore native USDC addresses, not the HyperEVM USDC contract addresses
  private readonly HYPERCORE_USDC_MAINNET = '0xeb62eee3685fc4c43992febcd9e75443';
  private readonly HYPERCORE_USDC_TESTNET = '0xeb62eee3685fc4c43992febcd9e75443'; // Verify if testnet uses same address

  /**
   * Note: Official UI uses regular USDC units (e.g., "10" for 10 USDC), not 8 decimals
   * The amount should be passed as-is
   */
  private formatAmount(amountStr: string): string {
    const amount = parseFloat(amountStr);
    if (isNaN(amount) || amount <= 0) {
      throw new Error('Invalid amount');
    }
    return amount.toString();
  }

  /**
   * Get API endpoint based on chain
   */
  private getApiEndpoint(hyperliquidChain: 'Mainnet' | 'Testnet'): string {
    return hyperliquidChain === 'Mainnet'
      ? 'https://api.hyperliquid.xyz/exchange'
      : 'https://api.hyperliquid-testnet.xyz/exchange';
  }

  /**
   * Sign SendAsset action using EIP-712
   * Note: Using the actual structure from Hyperliquid's official UI, not the outdated docs
   */
  private async signSendAssetAction(
    action: HyperCoreAction,
    signer: ethers.Signer
  ): Promise<HyperCoreSignature> {
    // Convert chainId from hex to number
    const chainId = parseInt(action.signatureChainId, 16);

    // EIP-712 domain (matches official UI)
    const domain = {
      name: 'HyperliquidSignTransaction',
      version: '1',
      chainId: chainId,
      verifyingContract: '0x0000000000000000000000000000000000000000',
    };

    // EIP-712 types (matches official UI structure)
    const types = {
      'HyperliquidTransaction:SendAsset': [
        { name: 'hyperliquidChain', type: 'string' },
        { name: 'destination', type: 'string' },
        { name: 'sourceDex', type: 'string' },
        { name: 'destinationDex', type: 'string' },
        { name: 'token', type: 'string' },
        { name: 'amount', type: 'string' },
        { name: 'fromSubAccount', type: 'string' },
        { name: 'nonce', type: 'uint64' },
      ],
    };

    // Message to sign (matches official UI - includes all fields)
    const value = {
      hyperliquidChain: action.hyperliquidChain,
      destination: action.destination,
      sourceDex: action.sourceDex || '', // Empty string if undefined (for perps)
      destinationDex: action.destinationDex,
      token: action.token,
      amount: action.amount,
      fromSubAccount: action.fromSubAccount === null ? '' : String(action.fromSubAccount),
      nonce: action.nonce,
    };

    // Sign the typed data using the signer
    const signature = await signer.signTypedData(domain, types, value);

    // Split signature into r, s, v components
    const sig = ethers.Signature.from(signature);

    return {
      r: sig.r,
      s: sig.s,
      v: sig.v,
    };
  }

  /**
   * Submit signed action to HyperCore exchange API
   */
  private async submitSendAsset(
    apiEndpoint: string,
    action: HyperCoreAction,
    signature: HyperCoreSignature
  ): Promise<any> {
    // Create action object - match official UI structure
    const actionPayload: any = {
      type: action.type,
      hyperliquidChain: action.hyperliquidChain,
      signatureChainId: action.signatureChainId,
      destination: action.destination,
      sourceDex: action.sourceDex || '', // Empty string for perps, "spot" for spot
      destinationDex: action.destinationDex,
      token: action.token,
      amount: action.amount,
      fromSubAccount: action.fromSubAccount === null ? '' : String(action.fromSubAccount),
      nonce: action.nonce,
    };

    const payload = {
      action: actionPayload,
      nonce: action.nonce, // Top-level nonce uses the same timestamp
      signature: signature,
    };

    // Log payload for debugging
    console.log('HyperCore API Payload:', JSON.stringify(payload, null, 2));

    const response = await fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (data.status === 'ok') {
      return data;
    } else {
      // Include more detailed error information
      const errorMessage = data.response?.data || data.message || JSON.stringify(data);
      throw new Error(`HyperCore withdrawal failed: ${errorMessage}`);
    }
  }

  /**
   * Withdraw USDC from HyperCore to HyperEVM
   * @param params Withdrawal parameters
   * @returns Promise that resolves when withdrawal is submitted
   */
  async withdrawUSDCFromHyperCore(params: HyperCoreWithdrawParams): Promise<any> {
    const {
      amount,
      sourceDex,
      hyperliquidChain,
      signatureChainId,
      signer,
    } = params;

    // Format amount (official UI uses regular units, not 8 decimals)
    const formattedAmount = this.formatAmount(amount);

    // Get HyperCore USDC address (different from HyperEVM USDC address!)
    const hypercoreUsdcAddress = hyperliquidChain === 'Mainnet' 
      ? this.HYPERCORE_USDC_MAINNET 
      : this.HYPERCORE_USDC_TESTNET;
    
    // Format token as "USDC:<address>" to match official UI (uses HyperCore address, not HyperEVM)
    const tokenField = `USDC:${hypercoreUsdcAddress}`;

    // Construct the sendAsset action
    const action: HyperCoreAction = {
      type: 'sendAsset',
      hyperliquidChain: hyperliquidChain,
      signatureChainId: signatureChainId,
      destination: this.USDC_SYSTEM_ADDRESS,
      sourceDex: sourceDex === 'spot' ? 'spot' : undefined, // Omit for perps
      destinationDex: 'spot', // Always 'spot' for HyperEVM withdrawals
      token: tokenField, // Format: "USDC:0x..." to match official UI
      amount: formattedAmount,
      fromSubAccount: null,
      nonce: Date.now(),
    };

    // Sign the action
    const signature = await this.signSendAssetAction(action, signer);

    // Submit the action
    const apiEndpoint = this.getApiEndpoint(hyperliquidChain);
    const result = await this.submitSendAsset(apiEndpoint, action, signature);

    return result;
  }
}

export const hypercoreService = new HyperCoreService();

